/**
 * PlanBillingPanel — seller SaaS subscription management.
 *
 * Mounted at `/seller/settings#plan-billing` (under Finance group,
 * owner-only). Buyer side gets a "Verqo is always free for buyers"
 * placeholder via the same component.
 *
 * Pricing model (Feb 2026 — 4-tier):
 *   • Starter    — $99/mo    + $30/buyer over 2 included
 *   • Growth     — $599/mo   + $25/buyer over 10 included
 *   • Pro        — $1,499/mo + $20/buyer over 50 included
 *   • Enterprise — $3,999/mo + $15/buyer over 100 included
 *
 * Active buyer = buyer-seller connection with `last_activity_at`
 * within the last 30 days. Dormant buyers don't count toward billing.
 *
 * Billing rails = the seller's existing verqo wallet (NOT Stripe).
 * Same topup + auto-topup flows already in place power the
 * subscription charges.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, Check, Wallet, Receipt, TrendingUp, Users, Clock,
  AlertCircle, Loader2, ArrowUpRight, ArrowDownRight, ShieldCheck,
} from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import AutoTopupCard from "./AutoTopupCard";
import OverageApprovalCard from "./OverageApprovalCard";

const fmt = (cents) =>
  cents == null ? "$0" : `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtCents = (cents) =>
  cents == null ? "$0.00" : `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_LABEL = {
  unsubscribed: "No active plan",
  active:       "Active",
  grace:        "Payment overdue · grace period",
  read_only:    "Read-only — pay to restore",
  suspended:    "Suspended",
  buyer_free:   "Always free",
};

const STATUS_TONE = {
  unsubscribed: "bg-amber-50 text-amber-700 border-amber-200",
  active:       "bg-emerald-50 text-emerald-700 border-emerald-200",
  grace:        "bg-amber-50 text-amber-700 border-amber-200",
  read_only:    "bg-rose-50 text-rose-700 border-rose-200",
  suspended:    "bg-rose-100 text-rose-800 border-rose-300",
  buyer_free:   "bg-emerald-50 text-emerald-700 border-emerald-200",
};


export default function PlanBillingPanel({ role = "seller" }) {
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [confirmPlan, setConfirmPlan] = useState(null);
  const [acting, setActing] = useState(false);
  const [billingInterval, setBillingInterval] = useState("monthly");

  const refresh = async () => {
    try {
      const [p, s, w] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/status"),
        api.get("/wallet").catch(() => ({ data: null })),
      ]);
      setPlans(p.data?.plans || []);
      setStatus(s.data || null);
      setWallet(w.data);
    } catch (e) {
      // 403 buyer → fall through to buyer card
    }
  };
  useEffect(() => { refresh(); }, []);

  // Buyer view — always-free message.
  if (role === "buyer" || status?.status === "buyer_free") {
    return (
      <div className="space-y-6">
        <SectionHeader title="Plan & Billing" desc="Your verqo account."/>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 max-w-2xl">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 grid place-items-center text-emerald-700 shrink-0">
              <Sparkles className="w-5 h-5"/>
            </div>
            <div className="flex-1">
              <div className="font-medium text-emerald-900">Verqo for buyers is always free.</div>
              <p className="text-[13px] text-emerald-900/75 mt-1.5 leading-relaxed">
                You're never charged a platform fee. Sellers pay a monthly subscription to access verqo's tools. You only ever move money on standard payment rails — no markup, no transaction fees, no per-buyer charges.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Seller view — full Plan & Billing.
  if (!status) {
    return (
      <div className="text-[13px] text-foreground/55 inline-flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin"/>Loading billing…
      </div>
    );
  }

  const isSubscribed = status.status === "active" || status.status === "grace" || status.status === "read_only";
  const currentPlan = status.plan;
  const walletBalanceCents = Math.round(((wallet?.balance) || 0) * 100);

  // Annual = 10% off the BASE fee only; overages always charged at full rate.
  const annualPct = status.annual_discount_pct ?? 0.10;
  // When already subscribed, the picker reflects the seller's locked-in
  // interval (changing cadence = resubscribe). New sellers can toggle.
  const effInterval = isSubscribed ? (status.interval || "monthly") : billingInterval;
  const annualMoEquiv = (baseCents) => Math.round(baseCents * (1 - annualPct));
  const annualTotal   = (baseCents) => Math.round(baseCents * 12 * (1 - annualPct));
  const chargedNow = (plan, interval) => {
    const overage = Math.max(0, status.active_buyers - plan.buyers_included) * plan.per_buyer_rate_cents;
    return interval === "annual"
      ? annualTotal(plan.base_fee_cents) + overage * 12
      : plan.base_fee_cents + overage;
  };

  const handleSubscribe = (plan) => setConfirmPlan({ plan, mode: "subscribe" });
  const handleChange    = (plan) => setConfirmPlan({ plan, mode: "change"    });

  const confirm = async () => {
    if (!confirmPlan?.plan) return;
    setActing(true);
    try {
      const path = confirmPlan.mode === "subscribe" ? "/billing/subscribe" : "/billing/change-plan";
      const payload = confirmPlan.mode === "subscribe"
        ? { plan_id: confirmPlan.plan.id, interval: effInterval }
        : { plan_id: confirmPlan.plan.id };
      const { data } = await api.post(path, payload);
      toast.success(confirmPlan.mode === "subscribe"
        ? `Subscribed to ${confirmPlan.plan.name}`
        : `Switched to ${confirmPlan.plan.name}`);
      setStatus(data);
      setConfirmPlan(null);
      // Refresh wallet too — charge just happened.
      api.get("/wallet").then(r => setWallet(r.data)).catch(() => {});
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "insufficient_wallet_for_subscription") {
        toast.error(d.message, { duration: 6000 });
      } else {
        toast.error(d?.message || d || "Could not complete the change");
      }
    } finally { setActing(false); }
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Plan & Billing"
        desc="Verqo charges a monthly subscription paid from your wallet. No transaction fees, no GMV cut. Buyers are always free."
      />

      {/* Current status card */}
      <div className="rounded-2xl border border-border bg-background overflow-hidden" data-testid="billing-current-status">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">Current plan</div>
            <span className={`chip ${STATUS_TONE[status.status] || "bg-secondary"} text-[10.5px] mono uppercase tracking-wider`}>
              {STATUS_LABEL[status.status] || status.status}
            </span>
          </div>
          {status.cycle_end && (
            <div className="text-[11.5px] text-foreground/55">
              Cycle ends {new Date(status.cycle_end).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
            </div>
          )}
        </div>

        <div className="p-5 grid md:grid-cols-3 gap-5">
          {/* Left: plan summary */}
          <div>
            {currentPlan ? (
              <>
                <div className="display text-2xl tracking-tight">{currentPlan.name}</div>
                <div className="text-[12.5px] text-foreground/55 mt-1">{currentPlan.tagline}</div>
                <div className="mt-3 text-[13px] mono">
                  {fmt(currentPlan.base_fee_cents)}<span className="text-foreground/55"> / mo</span>
                  <span className="text-foreground/55"> · {currentPlan.buyers_included} buyers included</span>
                </div>
                <div className="text-[11.5px] text-foreground/55 mt-1">
                  {fmt(currentPlan.per_buyer_rate_cents)} per additional active buyer
                </div>
              </>
            ) : (
              <>
                <div className="display text-2xl tracking-tight">No plan yet</div>
                <div className="text-[12.5px] text-foreground/55 mt-1">Subscribe below to unlock invoicing, payouts, and credit features.</div>
              </>
            )}
          </div>

          {/* Center: usage meter */}
          <div className="md:border-l md:border-border md:pl-5">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 inline-flex items-center gap-1.5">
              <Users className="w-3 h-3"/>This cycle's buyers
            </div>
            <div className="display text-3xl mt-1.5 tabular-nums">{status.active_buyers}</div>
            <div className="text-[11.5px] text-foreground/55 mt-1">
              active{status.dormant_buyers > 0 && <> · <span title="No activity in 30+ days — not billed">{status.dormant_buyers} dormant</span></>}
            </div>
            {currentPlan && status.active_buyers > currentPlan.buyers_included && (
              <div className="text-[11.5px] text-amber-700 mt-2 inline-flex items-center gap-1">
                <ArrowUpRight className="w-3 h-3"/>
                {status.active_buyers - currentPlan.buyers_included} over included → {fmt((status.active_buyers - currentPlan.buyers_included) * currentPlan.per_buyer_rate_cents)} overage
              </div>
            )}
          </div>

          {/* Right: next bill + wallet */}
          <div className="md:border-l md:border-border md:pl-5">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 inline-flex items-center gap-1.5">
              <Receipt className="w-3 h-3"/>Next bill (estimated)
            </div>
            <div className="display text-3xl mt-1.5 tabular-nums">{fmt(status.estimated_next_bill_cents)}</div>
            <div className="text-[11.5px] text-foreground/55 mt-1.5 inline-flex items-center gap-1.5">
              <Wallet className="w-3 h-3"/>
              Wallet: <span className="font-medium text-foreground/75">{fmtCents(walletBalanceCents)}</span>
            </div>
            {currentPlan && walletBalanceCents < (status.estimated_next_bill_cents || 0) && (
              <div className="text-[11.5px] text-rose-700 mt-1 inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3"/>Top up before {new Date(status.cycle_end).toLocaleDateString()} to avoid suspension
              </div>
            )}
          </div>
        </div>

        {/* Privacy + dunning footer */}
        <div className="px-5 py-3 border-t border-border bg-secondary/30 text-[11px] text-foreground/55 inline-flex items-start gap-1.5 w-full">
          <ShieldCheck className="w-3 h-3 mt-0.5 shrink-0"/>
          <span>
            Billing deducts from your verqo wallet on the cycle close date. If wallet runs short: 3-day grace → read-only mode → 14-day full suspension. We email you at every step.
          </span>
        </div>
      </div>

      {/* Plan picker */}
      <div>
        <h3 className="display text-lg tracking-tight">{isSubscribed ? "Switch plans" : "Choose a plan"}</h3>
        <p className="text-[12.5px] text-foreground/55 mt-1">
          {isSubscribed
            ? "Upgrades are prorated for the remainder of your cycle. Downgrades take effect next cycle."
            : "Pick a plan to start. The first period deducts from your wallet immediately."}
        </p>

        {/* Billing interval toggle */}
        <div className="mt-4 flex items-center gap-3 flex-wrap" data-testid="billing-interval-toggle">
          {isSubscribed ? (
            <span className="chip bg-secondary text-[11px] mono uppercase tracking-wider">
              Billed {effInterval === "annual" ? "annually" : "monthly"}
            </span>
          ) : (
            <div className="inline-flex items-center rounded-full border border-border p-0.5 bg-background">
              <button onClick={() => setBillingInterval("monthly")} data-testid="interval-monthly"
                className={`px-4 h-8 rounded-full text-[12.5px] transition-colors ${effInterval === "monthly" ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}>
                Monthly
              </button>
              <button onClick={() => setBillingInterval("annual")} data-testid="interval-annual"
                className={`px-4 h-8 rounded-full text-[12.5px] transition-colors inline-flex items-center gap-1.5 ${effInterval === "annual" ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}>
                Annual <span className={`text-[10px] ${effInterval === "annual" ? "text-background/80" : "text-emerald-700"}`}>save {Math.round(annualPct * 100)}%</span>
              </button>
            </div>
          )}
          {effInterval === "annual" && (
            <span className="text-[11.5px] text-foreground/50">{Math.round(annualPct * 100)}% off base · no discount on buyer overages</span>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-4" data-testid="billing-plans">
          {plans.map(p => {
            const isCurrent = currentPlan?.id === p.id;
            const moPrice = effInterval === "annual" ? annualMoEquiv(p.base_fee_cents) : p.base_fee_cents;
            return (
              <div key={p.id}
                className={`rounded-2xl border p-5 flex flex-col ${
                  isCurrent
                    ? "border-foreground bg-foreground/3 ring-2 ring-foreground/10"
                    : "border-border bg-background hover:border-foreground/30"
                }`}
                data-testid={`plan-card-${p.id}`}>
                <div className="flex items-center justify-between">
                  <div className="display text-xl tracking-tight">{p.name}</div>
                  {isCurrent && (
                    <span className="chip bg-foreground text-background text-[10px] mono uppercase tracking-wider">Current</span>
                  )}
                </div>
                <div className="text-[12px] text-foreground/55 mt-1">{p.tagline}</div>
                <div className="mt-3">
                  <span className="display text-3xl">{fmt(moPrice)}</span>
                  <span className="text-foreground/55 text-[13px]"> / month</span>
                </div>
                {effInterval === "annual" ? (
                  <div className="text-[11.5px] text-foreground/55 mt-1">
                    {fmt(annualTotal(p.base_fee_cents))} billed yearly · {p.buyers_included} buyers included
                  </div>
                ) : (
                  <div className="text-[11.5px] text-foreground/55 mt-1">
                    {p.buyers_included} buyers included · {fmt(p.per_buyer_rate_cents)} each additional
                  </div>
                )}
                <div className="text-[11px] text-foreground/45 mt-0.5">
                  {fmt(p.per_buyer_rate_cents)} per extra active buyer{effInterval === "annual" ? " · billed monthly, full rate" : ""}
                </div>
                <ul className="mt-4 space-y-1.5 text-[12.5px] flex-1">
                  {(p.features || []).map(f => (
                    <li key={f} className="flex items-start gap-1.5">
                      <Check className="w-3 h-3 mt-1 text-foreground/55 shrink-0"/>
                      <span className="text-foreground/75">{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4">
                  {isCurrent ? (
                    <Button disabled variant="outline" className="w-full rounded-full" data-testid={`plan-current-${p.id}`}>
                      Your current plan
                    </Button>
                  ) : isSubscribed ? (
                    <Button onClick={() => handleChange(p)}
                            className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90"
                            data-testid={`plan-change-${p.id}`}>
                      {p.base_fee_cents > (currentPlan?.base_fee_cents || 0) ? (
                        <><ArrowUpRight className="w-3.5 h-3.5 mr-1"/>Upgrade</>
                      ) : (
                        <><ArrowDownRight className="w-3.5 h-3.5 mr-1"/>Downgrade</>
                      )}
                    </Button>
                  ) : (
                    <Button onClick={() => handleSubscribe(p)}
                            className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90"
                            data-testid={`plan-subscribe-${p.id}`}>
                      Subscribe
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Auto-topup widget — only useful once the seller has an active plan. */}
      {isSubscribed && <AutoTopupCard/>}

      {/* Overage-approval opt-in toggle — owner-only safety valve. */}
      {isSubscribed && <OverageApprovalCard/>}

      {/* Confirm modal */}
      <Dialog open={!!confirmPlan} onOpenChange={(v) => !v && setConfirmPlan(null)}>
        <DialogContent className="max-w-md" data-testid="billing-confirm-modal">
          <DialogHeader>
            <DialogTitle>
              {confirmPlan?.mode === "subscribe" ? "Subscribe to " : "Switch to "}{confirmPlan?.plan?.name}
            </DialogTitle>
            <DialogDescription>
              {confirmPlan?.mode === "subscribe"
                ? (effInterval === "annual"
                    ? `We'll deduct the first year from your verqo wallet right now (base discounted ${Math.round(annualPct * 100)}%; overages billed at full rate). After that, you're billed yearly on the same day.`
                    : `We'll deduct the first month from your verqo wallet right now. After that, you're billed monthly on the same day.`)
                : `Upgrades are prorated for the remaining days in your cycle. Downgrades take effect next cycle (no immediate refund).`}
            </DialogDescription>
          </DialogHeader>
          {confirmPlan?.plan && (
            <div className="rounded-xl bg-secondary/40 border border-border px-4 py-3 space-y-1.5 text-[12.5px]">
              <div className="flex justify-between">
                <span className="text-foreground/55">Plan</span>
                <span className="font-medium">{confirmPlan.plan.name} · {effInterval === "annual" ? "Annual" : "Monthly"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground/55">{effInterval === "annual" ? `Base (yearly, ${Math.round(annualPct * 100)}% off)` : "Base monthly"}</span>
                <span className="mono">{effInterval === "annual" ? fmt(annualTotal(confirmPlan.plan.base_fee_cents)) : fmt(confirmPlan.plan.base_fee_cents)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground/55">Buyers included</span>
                <span className="mono">{confirmPlan.plan.buyers_included}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground/55">Each additional</span>
                <span className="mono">{fmt(confirmPlan.plan.per_buyer_rate_cents)}{effInterval === "annual" ? " / mo (full rate)" : " / month"}</span>
              </div>
              {confirmPlan.mode === "subscribe" && (
                <div className="flex justify-between border-t border-border pt-1.5 mt-1.5">
                  <span className="font-medium">Charged now</span>
                  <span className="font-medium display">
                    {fmt(chargedNow(confirmPlan.plan, effInterval))}
                  </span>
                </div>
              )}
              <div className="text-[11px] text-foreground/55 inline-flex items-center gap-1.5 pt-1">
                <Wallet className="w-3 h-3"/>
                Wallet balance: {fmtCents(walletBalanceCents)}
              </div>
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmPlan(null)} data-testid="billing-confirm-cancel">
              Cancel
            </Button>
            <Button disabled={acting}
                    className="rounded-full bg-foreground text-background"
                    onClick={confirm}
                    data-testid="billing-confirm-go">
              {acting
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/>Processing…</>
                : confirmPlan?.mode === "subscribe"
                  ? "Subscribe + charge wallet"
                  : "Confirm switch"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function SectionHeader({ title, desc }) {
  return (
    <div>
      <h2 className="display text-2xl tracking-tight">{title}</h2>
      {desc && <p className="text-[13px] text-foreground/55 mt-1.5 leading-relaxed max-w-3xl">{desc}</p>}
    </div>
  );
}
